import {Injectable} from '@angular/core';
import moment from 'moment';
import {Observable} from 'rxjs';
import {delay, filter, startWith, switchMap, switchMapTo, tap} from 'rxjs/operators';
import {dependOn} from '../../../common/rxjs/depend-on';
import {SecurityService} from '../../../components/security';
import {isAsecurityKey} from '../../../components/security/aspreader-security';
import {
    Bar,
    LibrarySymbolInfo,
    ResolutionString,
    SubscribeBarsCallback
} from '../../../components/trading-view-chart/charting-library/datafeed-api';
import {DataSubscribers} from '../../../components/trading-view-chart/datafeed/data-pulse-provider';
import {logMessage} from '../../../components/trading-view-chart/datafeed/helpers';
import {GetBarsResult} from '../../../components/trading-view-chart/datafeed/history-provider';
import {Candle} from '../../../models/candle';
import {formatSecurityKey, parseSecurityKey} from '../../../models/security-key';
import {createAsecurityCandlesStream} from './asecurity-candle-utils';
import {CandleStreamingService} from './candle-streaming';
import {ChartHistoryProvider} from './chart-history-provider';
import {CHART_RESOLUTIONS_DICT} from './chart-resolution';

@Injectable()
export class ChartDataPulseProvider {

    private readonly subscribers: DataSubscribers = {};

    public constructor(
        private historyProvider: ChartHistoryProvider,
        private candleStreamingService: CandleStreamingService,
        private securityService: SecurityService
    ) {}

    public subscribeBars(
        symbolInfo: LibrarySymbolInfo,
        resolution: ResolutionString,
        newDataCallback: SubscribeBarsCallback,
        listenerGuid: string
    ): void {
        if (this.subscribers[listenerGuid]) {
            logMessage(`DataPulseProvider: already has subscriber with id=${listenerGuid}`);
            return;
        }

        let lastCandle: Candle;

        const candles$: Observable<Candle> = this.createCandlesStream(symbolInfo, resolution);

        const restoreHistoryTrigger$ = this.candleStreamingService.disconnected$.pipe(
            switchMapTo(this.candleStreamingService.subscriptionSent$),
            filter(() => !!lastCandle),
            startWith(false)
        );

        const keepCandlesStream$ = candles$.pipe(
            tap(candle => lastCandle = candle)
        );

        const candlesAfterHistoricalDataUpdate = (): Observable<Candle> => {
            return this.historyProvider.getBarsStream(
                symbolInfo,
                resolution,
                moment.utc(lastCandle.closeTime).unix(),
                null
            ).pipe(
                tap((result: GetBarsResult) => {
                    result.bars.map(bar => this.pushReceivedBars(bar, listenerGuid));
                    this.candleStreamingService.getCandlesFromCache(symbolInfo.ticker, resolution).map(candle =>
                        this.updateData(candle)
                    );
                }),
                switchMapTo(candles$)
            );
        };

        const outputCandles$ = restoreHistoryTrigger$.pipe(
            dependOn(keepCandlesStream$),
            switchMap(wasDisconnected => wasDisconnected
                ? candlesAfterHistoricalDataUpdate()
                : candles$
            ),

            /**
             *  delay helps against call race that can happen when we subscribe
             *  on new symbol in first 10 seconds of candle period
             */
            delay(0),
            tap((candle: Candle) => this.updateData(candle))
        );

        this.subscribers[listenerGuid] = {
            lastBarTime:  null,
            listener:     newDataCallback,
            resolution,
            symbolInfo,
            subscription: outputCandles$.subscribe()
        };

        logMessage(`DataPulseProvider: subscribed for #${listenerGuid} - {${symbolInfo.name}, ${resolution}}`);
    }

    public unsubscribeBars(listenerGuid: string): void {
        this.subscribers[listenerGuid].subscription.unsubscribe();
        delete this.subscribers[listenerGuid];
        logMessage(`DataPulseProvider: unsubscribed for #${listenerGuid}`);
    }

    protected updateData(candle: Candle): void {
        if (candle) {
            const bar: Bar = ChartHistoryProvider.convertCandleToBar(candle);
            const barSecurityKey = formatSecurityKey(candle.security);

            Object.keys(this.subscribers).forEach(listenerGuid => {
                const subscriptionRecord = this.subscribers[listenerGuid];

                if (
                    subscriptionRecord && subscriptionRecord.symbolInfo.ticker === barSecurityKey
                    && CHART_RESOLUTIONS_DICT[subscriptionRecord.resolution].candlePeriod === candle.candlePeriod
                    && (this.historyProvider.lastProvidedBarTime || 0) <= bar.time
                    && (subscriptionRecord.lastBarTime || 0) <= bar.time
                ) {
                    subscriptionRecord.lastBarTime = bar.time;
                    subscriptionRecord.listener(bar);
                }
            });
        }
    }

    private pushReceivedBars(bar: Bar, listenerGuid: string): void {
        const subscriptionRecord = this.subscribers[listenerGuid];

        if (subscriptionRecord.lastBarTime <= bar.time) {
            subscriptionRecord.lastBarTime = bar.time;
            subscriptionRecord.listener(bar);
        }
    }

    private createCandlesStream(
        symbolInfo: LibrarySymbolInfo,
        resolution: ResolutionString
    ): Observable<Candle> {
        const securityKeyStr = symbolInfo.ticker;
        const securityKey = parseSecurityKey(securityKeyStr);

        const createCommonStream = (
            partSecurityKeyStr: string,
            partResolution: ResolutionString
        ): Observable<Candle> =>
            this.candleStreamingService.createSubscription(partSecurityKeyStr, partResolution);

        return isAsecurityKey(securityKey)
            ? createAsecurityCandlesStream(securityKey, resolution, createCommonStream, this.securityService)
            : createCommonStream(securityKeyStr, resolution);
    }

}
